import type { TransactionHistoryItem } from "@developerz.ai/ggcoin-sdk";
import { createMemo, createSignal, For, onCleanup, onMount, Show } from "solid-js";
import { useAuth } from "../auth/AuthContext.tsx";
import { ErrorBanner } from "../components/ErrorBanner.tsx";
import { Money } from "../components/Money.tsx";
import { formatTime } from "../lib/time.ts";

type KindFilter = "all" | "buy" | "transfer" | "spend" | "reversal";
type StatusFilter = "all" | "posted" | "pending" | "reversed";

const KIND_OPTIONS: KindFilter[] = ["all", "buy", "transfer", "spend", "reversal"];
const STATUS_OPTIONS: StatusFilter[] = ["all", "posted", "pending", "reversed"];

/**
 * Account transaction history (paginated, newest-first), kept live by the SSE
 * channel. A manual Refresh + client-side kind/status filters sit on top.
 */
export function History() {
  const { client } = useAuth();
  const [rows, setRows] = createSignal<TransactionHistoryItem[]>([]);
  const [nextBefore, setNextBefore] = createSignal<string | null>(null);
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal<unknown | null>(null);
  const [kindFilter, setKindFilter] = createSignal<KindFilter>("all");
  const [statusFilter, setStatusFilter] = createSignal<StatusFilter>("all");

  async function load(before?: string) {
    setLoading(true);
    setError(null);
    try {
      const page = await client.listTransactions(before ? { before } : undefined);
      setRows((prev) => (before ? [...prev, ...page.transactions] : page.transactions));
      setNextBefore(page.nextBefore);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  onMount(() => {
    void load();
    const unsubscribe = client.streamTransactions({
      onEvent: () => {
        void load();
      },
      onError: () => {},
    });
    onCleanup(() => {
      void Promise.resolve(unsubscribe()).catch(() => {});
    });
  });

  const visible = createMemo(() =>
    rows().filter(
      (tx) =>
        (kindFilter() === "all" || tx.kind === kindFilter()) &&
        (statusFilter() === "all" || tx.status === statusFilter()),
    ),
  );
  const filtered = () => kindFilter() !== "all" || statusFilter() !== "all";

  return (
    <section class="card">
      <div class="row" style={{ "align-items": "center", "justify-content": "space-between" }}>
        <h2 style={{ margin: 0 }}>Transaction history</h2>
        <button type="button" class="secondary small" disabled={loading()} onClick={() => load()}>
          {loading() ? "…" : "Refresh"}
        </button>
      </div>

      <div class="filter-bar">
        <label for="kind-filter">Type</label>
        <select
          id="kind-filter"
          value={kindFilter()}
          onChange={(e) => setKindFilter(e.currentTarget.value as KindFilter)}
        >
          <For each={KIND_OPTIONS}>
            {(k) => <option value={k}>{k === "all" ? "All types" : k}</option>}
          </For>
        </select>
        <label for="status-filter">Status</label>
        <select
          id="status-filter"
          value={statusFilter()}
          onChange={(e) => setStatusFilter(e.currentTarget.value as StatusFilter)}
        >
          <For each={STATUS_OPTIONS}>
            {(s) => <option value={s}>{s === "all" ? "All statuses" : s}</option>}
          </For>
        </select>
      </div>

      <ErrorBanner error={error()} />
      <Show
        when={visible().length > 0}
        fallback={
          <p class="muted" style={{ "margin-top": 0 }}>
            {loading()
              ? "Loading…"
              : filtered()
                ? "No transactions match these filters."
                : "No transactions yet — buy some coin to get started."}
          </p>
        }
      >
        <div class="table-scroll">
          <table class="table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Transaction</th>
              </tr>
            </thead>
            <tbody>
              <For each={visible()}>
                {(tx) => (
                  <tr>
                    <td>{tx.postedAt ? formatTime(tx.postedAt) : "—"}</td>
                    <td>
                      <span class="badge badge-active">{tx.kind}</span>
                    </td>
                    <td class="num">
                      <Money amount={tx.amount} />
                    </td>
                    <td>
                      <span class={`badge badge-${tx.status}`}>{tx.status}</span>
                    </td>
                    <td class="id-cell">{tx.id}</td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </Show>

      <Show when={nextBefore()}>
        <div class="row" style={{ "margin-top": "0.75rem" }}>
          <button
            type="button"
            class="secondary"
            disabled={loading()}
            onClick={() => load(nextBefore() ?? undefined)}
          >
            {loading() ? "Loading…" : "Load older"}
          </button>
        </div>
      </Show>
    </section>
  );
}
